/**
 * Stripe Terminal hardware catalog, loaded from data/hardware.json.
 * Edit the JSON to change items or prices; this file only types and indexes it.
 */
import { formatFiatCents } from '@coupling/shared-utils';
import catalog from './data/hardware.json';
import type { Accessory, HardwareSpecs, Reader } from './types';

export type { Accessory, AvailabilityStatus, HardwareSpecs, Reader } from './types';

// Import all hardware images directly so Vite processes and hashes them into dist.
// This is secure (no path traversal) and standard (Vite's asset handling).
import bbposWiseposE5ftCableImg from './images/bbpos-wisepos-e-5ft-cable.png';
import bbposWiseposEDockImg from './images/bbpos-wisepos-e-dock.png';
import bbposWiseposEImg from './images/bbpos-wisepos-e.png';
import stripeM2DockImg from './images/stripe-m2-dock.png';
import stripeM2MountImg from './images/stripe-m2-mount.png';
import stripeM2Img from './images/stripe-m2.png';
import stripeReaderChargerImg from './images/stripe-reader-charger.png';
import stripeReaderHubImg from './images/stripe-reader-hub.png';
import stripeS700Img from './images/stripe-s700.png';
import stripeS700S710CableImg from './images/stripe-s700-s710-cable.png';
import stripeS700S710CaseImg from './images/stripe-s700-s710-case.png';
import stripeS700S710DockImg from './images/stripe-s700-s710-dock.png';
import stripeS710Img from './images/stripe-s710.png';
import stripeT600Img from './images/stripe-t600.png';

// Map filenames to their imported URLs
const imageMap = new Map<string, string>([
  ['bbpos-wisepos-e-5ft-cable.png', bbposWiseposE5ftCableImg],
  ['bbpos-wisepos-e-dock.png', bbposWiseposEDockImg],
  ['bbpos-wisepos-e.png', bbposWiseposEImg],
  ['stripe-m2-dock.png', stripeM2DockImg],
  ['stripe-m2-mount.png', stripeM2MountImg],
  ['stripe-m2.png', stripeM2Img],
  ['stripe-reader-charger.png', stripeReaderChargerImg],
  ['stripe-reader-hub.png', stripeReaderHubImg],
  ['stripe-s700.png', stripeS700Img],
  ['stripe-s700-s710-cable.png', stripeS700S710CableImg],
  ['stripe-s700-s710-case.png', stripeS700S710CaseImg],
  ['stripe-s700-s710-dock.png', stripeS700S710DockImg],
  ['stripe-s710.png', stripeS710Img],
  ['stripe-t600.png', stripeT600Img],
]);

export const readers = catalog.readers as unknown as Reader[];
export const accessories = catalog.accessories as unknown as Accessory[];
export const paymentTypes: string[] = catalog.payment_types_all_readers;

const names = new Map<string, string>(
  [...readers, ...accessories].map((item) => [item.id, item.name])
);
export const nameOf = (id: string): string => names.get(id) ?? id;

export const imageSrc = (file: string): string => {
  const url = imageMap.get(file);
  if (!url) {
    console.warn(`Hardware image not found: ${file}`);
    return '';
  }
  return url;
};

/**
 * TODO(#2289): Coupling merchant pricing for hardware is not decided (markup,
 * bundle, rental, or subsidy). `price_cents` is Stripe's US list price before tax
 * and shipping. Change the displayed price here, in one place, once it is set.
 */
export const formatPrice = (priceCents: number): string => formatFiatCents(priceCents, 'USD');

export const PRICE_FOOTNOTE = 'Stripe US list price, before tax and shipping.';

export const TRADEMARK_DISCLAIMER =
  'Stripe, BBPOS and WisePOS are trademarks of their respective owners. Hardware specifications are provided by Stripe and may change.';

/** Limited-availability readers get a warning badge; others with a note get an info note. */
export const isLimited = (reader: Reader): boolean => reader.availability === 'limited';

/** Connectivity badges derived from the catalog fields. */
export const connectivityBadges = (reader: Reader): string[] => {
  const text = reader.connectivity.join(' ').toLowerCase();
  const badges: string[] = [];
  if (text.includes('wifi')) badges.push('WiFi');
  if (text.includes('ethernet')) badges.push('Ethernet');
  if (reader.cellular || text.includes('cellular')) badges.push('Cellular');
  if (reader.bluetooth || text.includes('bluetooth')) badges.push('Bluetooth');
  if (reader.offline_mode) badges.push('Offline mode');
  return badges;
};

const SPEC_LABELS: Record<string, string> = {
  display: 'Screen',
  battery: 'Battery',
  dimensions: 'Dimensions',
  weight: 'Weight',
  os: 'OS',
  power: 'Power',
  processor: 'Processor',
  memory_storage: 'Memory / storage',
  camera: 'Camera',
  audio_jack: 'Audio jack',
  security: 'Security',
  mounting: 'Mounting',
};
const SPEC_ORDER = Object.keys(SPEC_LABELS);

export const specRows = (specs: HardwareSpecs = {}): Array<[string, string]> =>
  Object.entries(specs)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => (SPEC_ORDER.indexOf(a) + 1 || 99) - (SPEC_ORDER.indexOf(b) + 1 || 99))
    .map(([key, value]) => [
      SPEC_LABELS[key] ?? key.replace(/_/g, ' '),
      typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value),
    ]);

const BEST_FOR: Record<string, string> = {
  'stripe-t600': 'Fixed checkout counter with a customer-facing screen',
  'stripe-s710': 'Tableside, pop-ups and markets, or spotty WiFi',
  'stripe-s700': 'Countertop or tableside with good WiFi',
  'stripe-m2': 'Low-cost mobile, paired with our phone or tablet app',
  'bbpos-wisepos-e': 'Countertop (limited availability)',
};
export const bestFor = (reader: Reader): string => BEST_FOR[reader.id] ?? reader.use_cases.join(', ');
